import { z } from "zod";
import {
    MAX_STRIPE_EXPRESS_LOGO_BASE64_LENGTH,
    STRIPE_EXPRESS_LOGO_MIME_TYPES,
} from "@common/constants/domains/stripe/stripe-express-branding";
import { isLikelyBase64 } from "@common/validators/is-likely-base64";

// Stripe stores branding colors as `#RRGGBB` and rejects anything else. The
// shared `HEX_COLOR_REGEX` leaves the leading `#` optional, so a value like
// "abcdef" would pass that check and only fail at Stripe — surfacing as a 500
// instead of a clean 400. Require the hash here so validation catches it.
const STRIPE_HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;

// Where the user launched the Account Link from. Drives the Stripe
// `return_url` (which frontend surface to reopen) and the `refresh_url`
// (which remints with the same context). Defaults to `integrations`.
export enum StripeExpressReturnTo {
    INVOICE = "invoice",
    ACCOUNTS = "accounts",
    INTEGRATIONS = "integrations",
}

// Body for create-or-resume: creates the entity's Express account on first use
// and remints (un-archiving if needed) on every subsequent call. `invoiceId` is
// only meaningful when `returnTo === invoice`, so the drawer can be reopened.
// The target business is `defaultEntityId` (not `entityId`) — same convention as
// the OAuth Stripe Connect create: these endpoints authorize the entity
// themselves before any Stripe call, so they intentionally sit outside the
// EntityAccessGuard resource-pairing check keyed on a literal `entityId`.
export const stripeExpressCreateBodySchema = z.object({
    defaultEntityId: z.number().int().positive(),
    returnTo: z
        .nativeEnum(StripeExpressReturnTo)
        .default(StripeExpressReturnTo.INTEGRATIONS),
    invoiceId: z.string().uuid().optional(),
});

export type StripeExpressCreateBody = z.infer<
    typeof stripeExpressCreateBodySchema
>;

// Body for Finish setup on an existing Express connection.
export const stripeExpressRemintBodySchema = z.object({
    returnTo: z
        .nativeEnum(StripeExpressReturnTo)
        .default(StripeExpressReturnTo.INTEGRATIONS),
    invoiceId: z.string().uuid().optional(),
});

export type StripeExpressRemintBody = z.infer<
    typeof stripeExpressRemintBodySchema
>;

// Path params shared by every per-connection Express route (`:connectionId`).
// Defined here rather than inline in the contract so contract files stay a pure
// route map (repo convention: Zod schemas live in `*.schema.ts`).
export const stripeExpressConnectionParamsSchema = z.object({
    connectionId: z.coerce.number(),
});

// Account Links expire in ~5 minutes, so this hosted URL is minted on every
// click and never persisted.
export const stripeExpressAccountLinkResponseSchema = z.object({
    url: z.string().url(),
});

export type StripeExpressAccountLinkResponse = z.infer<
    typeof stripeExpressAccountLinkResponseSchema
>;

// Return-page toast source: a live `accounts.retrieve` writes the cache and
// echoes the freshly read flags so the SPA never toasts from a stale list.
export const stripeExpressRefreshCacheResponseSchema = z.object({
    detailsSubmitted: z.boolean(),
    chargesEnabled: z.boolean(),
});

export type StripeExpressRefreshCacheResponse = z.infer<
    typeof stripeExpressRefreshCacheResponseSchema
>;

export const stripeExpressStartOverBodySchema = z.object({}).optional();

export const stripeExpressStartOverResponseSchema = z.object({
    isAbandoned: z.literal(true),
});

export type StripeExpressStartOverResponse = z.infer<
    typeof stripeExpressStartOverResponseSchema
>;

// Minimal readiness shape read from an `account.updated` Connect event object.
// Parsed (never cast) so the webhook handler stays type-safe against Stripe's
// empty `Event.Data.Object` interface. Only the fields the Express cache
// persists are read; everything else on the account is ignored.
export const stripeAccountReadinessSchema = z.object({
    id: z.string(),
    details_submitted: z.boolean().nullish(),
    charges_enabled: z.boolean().nullish(),
});

export type StripeAccountReadiness = z.infer<
    typeof stripeAccountReadinessSchema
>;

// Branding settings query/save for a Kick-created Express account. Every
// connected account (Express) can carry Stripe-hosted branding — logo, brand
// (primary) color, accent (secondary) color — shown on Stripe Checkout, hosted
// invoices, and receipts. Naming mirrors Stripe's own Dashboard labels
// (`primary_color` -> brand color, `secondary_color` -> accent color) rather
// than the raw API field names, since this is what an admin configuring their
// own connected account expects to see.
export const stripeExpressBrandingSchema = z.object({
    // Public HTTPS URL Kick can render directly (proxied through a Stripe file
    // link when a logo/icon is set); null when Stripe has no logo on file.
    logoUrl: z.string().url().nullable(),
    brandColor: z.string().regex(STRIPE_HEX_COLOR_REGEX).nullable(),
    accentColor: z.string().regex(STRIPE_HEX_COLOR_REGEX).nullable(),
});

export type StripeExpressBranding = z.infer<typeof stripeExpressBrandingSchema>;

// Save body: every field is an explicit tri-state so the client can (a) leave
// a field untouched by omitting it, (b) clear it by sending null, or (c) set
// it. `logo` is a base64-encoded image (not a URL/path) because it is
// uploaded straight to Stripe's Files API server-side — Kick never stores the
// image itself. Colors require a `#RRGGBB` hex string (see
// `STRIPE_HEX_COLOR_REGEX`), the shape Stripe accepts.
export const stripeExpressUpdateBrandingBodySchema = z.object({
    logo: z
        .object({
            data: z
                .string()
                .max(MAX_STRIPE_EXPRESS_LOGO_BASE64_LENGTH, {
                    message: "logo.data exceeds the maximum allowed size",
                })
                .refine(isLikelyBase64, {
                    message: "logo.data must be a valid base64 string",
                }),
            mimeType: z.enum(STRIPE_EXPRESS_LOGO_MIME_TYPES),
        })
        .nullable()
        .optional(),
    brandColor: z.string().regex(STRIPE_HEX_COLOR_REGEX).nullable().optional(),
    accentColor: z.string().regex(STRIPE_HEX_COLOR_REGEX).nullable().optional(),
});

export type StripeExpressUpdateBrandingBody = z.infer<
    typeof stripeExpressUpdateBrandingBodySchema
>;
